import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { useRealtorContext, Affiliate } from '../context/RealtorContext';
import Navbar from '../components/layout/Navbar';
import AffiliateFormModal from '../components/realtor/AffiliateFormModal';
import { UserCircleIcon, AcademicCapIcon, SparklesIcon, MagnifyingGlassIcon } from '@heroicons/react/24/solid'; // Import SVG icons

// Define the interface for the raw data received from the backend
interface RawAffiliateData {
  id: string;
  name: string;
  email: string;
  phone?: string;
  image?: string;
  created_at: string; // Backend field for joinDate
  upline_id?: string; // Backend field for referredBy
  // Add any other fields that the backend sends but are not directly in Affiliate
  // e.g., properties_count, commission_amount if they exist
  properties_count?: number;
  commission_amount?: number;
  level?: 1 | 2; // If backend sends it
  password?: string; // Add password field
  imageFile?: File | null; // For client-side handling of uploaded image
  imageSvg?: string | null; // For client-side handling of selected SVG
}

const AffiliateManagementPage: React.FC = () => {
  const { currentUser } = useRealtorContext();
  const [affiliates, setAffiliates] = useState<Affiliate[]>([]);
  const [search, setSearch] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingAffiliate, setEditingAffiliate] = useState<Affiliate | null>(null);
  const formModalRef = useRef<HTMLDivElement>(null);

  // Fetch affiliates from backend
  const fetchAffiliates = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        return;
      }
      const response = await axios.get<RawAffiliateData[]>('https://uniquestatebackend.gomsolutions.com.ng/api/affiliates', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setAffiliates(response.data.map((aff: RawAffiliateData) => ({
        id: aff.id,
        name: aff.name,
        email: aff.email,
        phone: aff.phone,
        image: aff.image,
        joinDate: aff.created_at ? new Date(aff.created_at).toISOString().split('T')[0] : '',
        properties: aff.properties_count || 0, // Use actual backend data or default
        commission: aff.commission_amount || 0, // Use actual backend data or default
        referredBy: aff.upline_id,
        level: aff.level || 1, // Use actual backend data or default
      })));
    } catch (error) {
      console.error('Error fetching affiliates:', error);
    }
  }, []);

  useEffect(() => {
    fetchAffiliates();
  }, [fetchAffiliates]);

  // Live search filter
  const filteredAffiliates = affiliates.filter(aff =>
    aff.name.toLowerCase().includes(search.toLowerCase()) ||
    aff.email.toLowerCase().includes(search.toLowerCase())
  );

  // CRUD: Add Affiliate
  const handleAddAffiliate = async (affiliateData: Affiliate, profileImage: File | null, selectedSvg: string | null) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        return;
      }

      const formData = new FormData();
      formData.append('name', affiliateData.name);
      formData.append('email', affiliateData.email);
      formData.append('phone', affiliateData.phone || '');
      formData.append('password', affiliateData.password || 'defaultpassword'); // Use provided password or default
      formData.append('role', 'realtor'); // New registrations via affiliate link are for realtors
      formData.append('joinDate', new Date().toISOString().split('T')[0]);
      formData.append('commissionHistory', JSON.stringify([])); // Initialize commissionHistory
      if (profileImage) {
        formData.append('image', profileImage);
      } else if (selectedSvg) {
        formData.append('imageSvg', selectedSvg); // Send SVG name if selected
      }
      if (affiliateData.referredBy) {
        formData.append('upline_id', affiliateData.referredBy);
      }

      console.log('Sending data to backend for add:', Object.fromEntries(formData.entries()));

      await axios.post('https://uniquestatebackend.gomsolutions.com.ng/api/affiliates', formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data', // Important for file uploads
        },
      });
      setShowFormModal(false);
      fetchAffiliates(); // Refresh list after adding
    } catch (error) {
      console.error('Error adding affiliate:', error);
    }
  };

  // CRUD: Update Affiliate
 // CRUD: Update Affiliate
 const handleUpdateAffiliate = async (updatedAffiliate: Affiliate, profileImage: File | null, selectedSvg: string | null) => {
   try {
     console.log('Attempting to update affiliate with ID:', updatedAffiliate.id);
     const token = localStorage.getItem('token');
     if (!token) {
       console.error('No token found');
       return;
     }

     const formData = new FormData();
     formData.append('name', updatedAffiliate.name);
     formData.append('email', updatedAffiliate.email);
     formData.append('phone', updatedAffiliate.phone || '');
     if (updatedAffiliate.password) { // Only send password if it's provided (i.e., changed)
       formData.append('password', updatedAffiliate.password);
     }
     if (profileImage) {
       formData.append('image', profileImage);
     } else if (selectedSvg) {
       formData.append('imageSvg', selectedSvg);
     } else if (updatedAffiliate.image) { // If no new image/svg, but existing image, keep it
       formData.append('image', updatedAffiliate.image);
     } else { // If no image/svg at all, explicitly send null or empty string
       formData.append('image', '');
     }
     if (updatedAffiliate.referredBy) {
       formData.append('upline_id', updatedAffiliate.referredBy);
     }

     console.log('Sending data to backend for update:', Object.fromEntries(formData.entries()));

     await axios.put(`https://uniquestatebackend.gomsolutions.com.ng/api/affiliates/${updatedAffiliate.id}`, formData, {
       headers: {
         Authorization: `Bearer ${token}`,
         'Content-Type': 'multipart/form-data', // Important for file uploads
       },
     });
     setShowFormModal(false);
     setEditingAffiliate(null);
     fetchAffiliates(); // Refresh list after updating
   } catch (error) {
     console.error('Error updating affiliate:', error);
   }
 };

 // CRUD: Delete Affiliate
 const handleDeleteAffiliate = async (id: string) => {
   if (!window.confirm('Are you sure you want to delete this affiliate?')) {
     return;
   }
   try {
     const token = localStorage.getItem('token');
     if (!token) {
       console.error('No token found');
       return;
     }
     await axios.delete(`https://uniquestatebackend.gomsolutions.com.ng/api/affiliates/${id}`, {
       headers: {
         Authorization: `Bearer ${token}`,
       },
     });
     fetchAffiliates(); // Refresh list after deleting
   } catch (error) {
     console.error('Error deleting affiliate:', error);
   }
 };

  // Modal close on outside click or ESC
  useEffect(() => {
    if (!showFormModal) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowFormModal(false);
        setEditingAffiliate(null);
      }
    };
    const handleClick = (e: MouseEvent) => {
      if (formModalRef.current && !formModalRef.current.contains(e.target as Node)) {
        setShowFormModal(false);
        setEditingAffiliate(null);
      }
    };
    document.addEventListener('keydown', handleKey);
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [showFormModal]);

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Navbar />
      <div className="flex-1 p-6 mt-24 max-w-7xl mx-auto w-full">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <h1 className="text-3xl font-bold text-primary">Affiliate Management</h1>
          {currentUser?.role === 'admin' && (
            <Button variant="primary" onClick={() => { setEditingAffiliate(null); setShowFormModal(true); }} className="w-full sm:w-auto">+ Add Affiliate</Button>
          )}
        </div>
        <div className="stickyx top-24 z-10 bg-gray-50 pb-4 mb-6">
          <input
            className="border rounded px-4 py-2 w-full max-w-xl shadow-sm focus:ring-2 focus:ring-primary/30 focus:border-primary transition"
            placeholder="Search affiliates..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            aria-label="Search affiliates"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {filteredAffiliates.length === 0 && (
            <div className="text-gray-400 text-center py-12 col-span-full">
              {currentUser?.role !== 'admin' ? (
                <div className="flex flex-col items-center justify-center">
                  <MagnifyingGlassIcon className="w-16 h-16 text-gray-300 mb-4" />
                  <p className="text-lg">You have no affiliate registered under you yet, kindly share your link to attract them.</p>
                </div>
              ) : (
                <p>No affiliates found.</p>
              )}
            </div>
          )}
          {filteredAffiliates.map(aff => (
            <Card
              key={aff.id}
              className="transition-shadow shadow-md hover:shadow-xl rounded-2xl p-6 flex flex-col items-center border border-gray-200 bg-white relative group"
            >
              {aff.image && (aff.image.startsWith('/uploads/') || aff.image.startsWith('http')) ? (
                <img
                  src={"http://localhost:5000"+aff.image || '/images/footer_logo.png'}
                  alt={aff.name}
                  className="w-20 h-20 rounded-full object-cover border-2 border-primary mb-3 shadow-sm"
                />
              ) : (
                <div className="w-20 h-20 rounded-full flex items-center justify-center bg-gray-200 border-2 border-primary mb-3 shadow-sm">
                  {(() => {
                    switch (aff.image) {
                      case 'UserCircleIcon': return <UserCircleIcon className="w-16 h-16 text-neutral-600" />;
                      case 'AcademicCapIcon': return <AcademicCapIcon className="w-16 h-16 text-neutral-600" />;
                      case 'SparklesIcon': return <SparklesIcon className="w-16 h-16 text-neutral-600" />;
                      default: return <UserCircleIcon className="w-16 h-16 text-neutral-600" />; // Default avatar
                    }
                  })()}
                </div>
              )}
              <div className="font-semibold text-lg text-center mb-1">{aff.name}</div>
              <div className="text-sm text-gray-500 text-center mb-1">{aff.email}</div>
              <div className="text-xs text-gray-400 text-center mb-2">Joined: {aff.joinDate}</div>
              <div className="flex flex-wrap gap-2 justify-center text-xs mb-3">
                <span className="bg-primary/10 text-primary px-2 py-1 rounded">Level {aff.level}</span>
                <span className="bg-green-100 text-green-700 px-2 py-1 rounded">{aff.properties} Properties</span>
                <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded">₦{aff.commission.toLocaleString()} Commission</span>
              </div>
              <div className="flex gap-2 mt-auto opacity-0 group-hover:opacity-100 transition-opacity">
                <Button variant="outline" size="sm" onClick={() => { setEditingAffiliate(aff); setShowFormModal(true); }}>Edit</Button>
                <Button variant="danger" size="sm" onClick={() => handleDeleteAffiliate(aff.id)}>Delete</Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
      <AffiliateFormModal
        isOpen={showFormModal}
        onClose={() => { setShowFormModal(false); setEditingAffiliate(null); }}
        onSubmit={editingAffiliate ? handleUpdateAffiliate : handleAddAffiliate}
        initialData={editingAffiliate ? { ...editingAffiliate, imageFile: null, imageSvg: editingAffiliate.image?.startsWith('http') ? null : editingAffiliate.image } : null}
      />
    </div>
  );
};

export default AffiliateManagementPage;
